import React, { Suspense, lazy } from 'react'
import PropTypes from 'prop-types'
import { compareJSON } from 'utils/object'
import { CONSENT_ITEM_EDITOR_ENABLED_QUESTION_TYPES_ARR } from 'utils/constants'
import { Loader } from 'components/UIElements'
import SurveyToolbar from '../../../../../../Instruments/routes/Instrument/routes/EditSurvey/components/SurveyToolbar'

const NewConsentItemList = lazy(() => import(/* webpackChunkName: "dnd-kit-consent" */ './NewConsentItemList'))
import NewConsentItemEditor from './NewConsentItemEditor'
import TranslationSelection from './TranslationSelection/TranslationSelection'
import '../stylesheets/NewConsentEditor.scss'
import '../../../../../../Instruments/routes/Instrument/routes/EditSurvey/stylesheets/Survey.scss'
import { getRandomQuestionId } from '../../../../../../../utils/getRandomQuestionId'

export default class NewConsentBuilderPage extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      activeItemId: null,
      currentLang: 'en_US',
      hasConsentError: false,
      itemEditorExpanded: true,
      showTranslationPanel: false,
    }
  }

  componentDidMount() {
    const { consent } = this.props
    const hasTranslations = consent?.metadata?.other_languages?.length > 0
    this.setState({
      activeItemId: consent.order[0][0],
      showTranslationPanel: hasTranslations,
    })
  }

  componentDidUpdate(prevProps) {
    const { errors } = this.props
    if (!compareJSON(errors.questions, prevProps.errors.questions))
      this.setState({ currentLang: 'en_US', hasConsentError: true })
  }

  onMoveItem = ({ oldIndex, newIndex }) => {
    this.props.moveItem(this.props.consent, oldIndex, newIndex)
  }

  onDeleteItem = itemId => {
    return () => {
      this.props.deleteItem(this.props.consent, itemId)
    }
  }

  onChangeItemType = (itemId, prevType, nextType) => {
    this.props.changeItemType(this.props.consent, itemId, prevType, nextType, this.props.isClinro)
  }

  onDeleteChoice = (itemId, choiceId) => {
    return () => {
      this.props.deleteChoice(this.props.consent, itemId, choiceId)
    }
  }

  onAddItem = (itemType, index) => {
    return e => {
      const newQId = getRandomQuestionId()
      this.props.addItem({
        itemType,
        index,
        newQId,
        isClinro: this.props.isClinro,
      })
      this.onToggleItemEditor(true)
      this.setState({ activeItemId: newQId })
    }
  }

  onToggleEdit = itemId => {
    if (this.state.activeItemId !== itemId) {
      this.setState({ activeItemId: itemId })
    }
  }

  itemEditorEnabled = id => {
    const question = this.props.consent.questions[id]
    if (question && CONSENT_ITEM_EDITOR_ENABLED_QUESTION_TYPES_ARR.includes(question.type)) {
      return true
    }
    return false
  }

  onToggleItemEditor = (toggleOn = false) => {
    const { itemEditorExpanded } = this.state
    if (toggleOn) {
      this.setState({ itemEditorExpanded: toggleOn })
    } else {
      this.setState({ itemEditorExpanded: !itemEditorExpanded })
    }
  }

  setCurrentLang = langKey => {
    this.setState({ currentLang: langKey })
  }

  shouldCancelStart(e) {
    return e.target.className !== 'grab-icon'
  }

  onToggleTranslationPanel = () => {
    this.setState(prevState => ({ showTranslationPanel: !prevState.showTranslationPanel }))
  }

  dismissConsentError = () => {
    this.setState({ hasConsentError: false })
  }

  render() {
    const {
      addConsentTranslation,
      clearAllConsentTranslations,
      consent,
      deleteConsentTranslation,
      duplicateItem,
      enforcedLanguages,
      isConsentTwo,
      locales,
      localesMap,
      studyID,
      version,
      study,
      userId,
    } = this.props
    const { activeItemId, currentLang, hasConsentError, itemEditorExpanded, showTranslationPanel } = this.state
    const { pdf_upload: pdfUpload } = consent
    const hideTranslationPanel = !isConsentTwo || !showTranslationPanel
    return (
      <div className='e-consent'>
        <div className='survey-builder'>
          <SurveyToolbar
            studyID={studyID}
            id={consent.id}
            version={version}
            {...this.props}
            isConsent
            isConsentTwo={isConsentTwo}
            onToggleTranslationPanel={isConsentTwo ? this.onToggleTranslationPanel : undefined}
            showTranslationPanel={showTranslationPanel}
          />
          <div
            className={`builder-content${hideTranslationPanel ? ' translations-hidden' : ''}${
              !itemEditorExpanded ? ' item-editor-collapsed' : ''
            } flexed start-aligned start-justified`}>
            {isConsentTwo && showTranslationPanel && (
              <div className='left-section'>
                <h4>Languages</h4>
                <TranslationSelection
                  addConsentTranslation={addConsentTranslation}
                  clearAllConsentTranslations={clearAllConsentTranslations}
                  consent={consent}
                  currentLang={currentLang}
                  deleteConsentTranslation={deleteConsentTranslation}
                  dismissConsentError={this.dismissConsentError}
                  enforcedLanguages={enforcedLanguages}
                  hasConsentError={hasConsentError}
                  locales={locales}
                  localesMap={localesMap}
                  setCurrentLang={this.setCurrentLang}
                />
              </div>
            )}
            <div className='main-editor-section'>
              <Suspense
                fallback={
                  <div className='flexed center-justified' style={{ minHeight: 120 }}>
                    <Loader inContainer size={24} />
                  </div>
                }>
                <NewConsentItemList
                  {...this.props}
                  activeItemId={activeItemId}
                  currentLang={currentLang}
                  onSortEnd={this.onMoveItem}
                  shouldCancelStart={this.shouldCancelStart}
                  lockToContainerEdges
                  helperClass='sortableHelper sortable-items'
                  lockAxis='y'
                  onToggleEdit={this.onToggleEdit}
                  onDeleteItem={this.onDeleteItem}
                  onChangeItemType={this.onChangeItemType}
                  onDeleteChoice={this.onDeleteChoice}
                  onAddItem={this.onAddItem}
                  onToggleItemEditor={this.onToggleItemEditor}
                />
              </Suspense>
              <div className={`right-section${this.itemEditorEnabled(activeItemId) ? ' active' : ''}`}>
                {consent.questions[activeItemId] &&
                  CONSENT_ITEM_EDITOR_ENABLED_QUESTION_TYPES_ARR.includes(consent.questions[activeItemId]?.type) && (
                    <NewConsentItemEditor
                      activeItemId={activeItemId}
                      consent={consent}
                      duplicateItem={duplicateItem}
                      onDeleteItem={this.onDeleteItem}
                      question={consent.questions[activeItemId]}
                      onToggleItemEditor={this.onToggleItemEditor}
                      itemEditorExpanded={itemEditorExpanded}
                      study={study}
                      userId={userId}
                    />
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

NewConsentBuilderPage.propTypes = {
  activeItem: PropTypes.object,
  activeItemId: PropTypes.string,
  addChoiceOnPaste: PropTypes.func,
  addConsentTranslation: PropTypes.func,
  addItem: PropTypes.func,
  changeItemType: PropTypes.func,
  clearAllConsentTranslations: PropTypes.func,
  consent: PropTypes.object,
  deleteChoice: PropTypes.func,
  deleteConsentTranslation: PropTypes.func,
  deleteItem: PropTypes.func,
  duplicateItem: PropTypes.func,
  enforcedLanguages: PropTypes.array,
  errors: PropTypes.shape({
    questions: PropTypes.object,
  }),
  initializeBlankConsent: PropTypes.func,
  isConsentTwo: PropTypes.bool,
  locales: PropTypes.array,
  localesMap: PropTypes.object,
  moveItem: PropTypes.func,
  noQuestionsErr: PropTypes.string,
  openModal: PropTypes.func,
  originalSurvey: PropTypes.object,
  params: PropTypes.shape({ studyID: PropTypes.string }),
  randomizeQuestions: PropTypes.func,
  saveConsent: PropTypes.func,
  studyID: PropTypes.number,
  titleErr: PropTypes.string,
  toggleEdit: PropTypes.func,
  updateTitle: PropTypes.func,
  version: PropTypes.number,
  versionOptions: PropTypes.array,
  wordCount: PropTypes.number,
}
